import { UNKNOWN_EVENT_LABEL } from "../api/parse.ts";
import { formatSessionDateLabelFromKey, sessionLocalDateKey } from "./sessionWhen.ts";

export type AuditRow = {
  eventId: string;
  type: string;
  occurredAt: string;
  who: string;
  whoGuid?: string | null;
  whoSource?: "snapshot" | "directory" | null;
  deviceId: string | null;
  sessionId: string | null;
  detail: string;
  requestId?: string | null;
};

export const AUDIT_FILTER_COLUMNS = ["when", "who", "type", "device", "session", "summary"] as const;

export type AuditFilterColumn = (typeof AUDIT_FILTER_COLUMNS)[number];

export type AuditColumnFilters = {
  readonly values: ReadonlyMap<AuditFilterColumn, ReadonlySet<string>>;
};

export const NO_AUDIT_COLUMN_FILTERS: AuditColumnFilters = { values: new Map() };

export type AuditColumnSortDirection = "asc" | "desc";

export type AuditColumnSort = { readonly column: AuditFilterColumn; readonly direction: AuditColumnSortDirection };

export const DEFAULT_AUDIT_COLUMN_SORT: AuditColumnSort = { column: "when", direction: "desc" };

export type AuditTableContext = {
  readonly timeZone?: string;
  readonly deviceNamesToken: string;
  readonly deviceNameFor: (deviceId: string | null) => string;
};

const collator = new Intl.Collator(undefined, { numeric: true, sensitivity: "base" });

function typeLabel(type: string): string {
  return type === "unknown" ? UNKNOWN_EVENT_LABEL : type;
}

export function auditFilterDisplayLabel(column: AuditFilterColumn, value: string): string {
  if (column === "when" && /^\d{4}-\d{2}-\d{2}$/.test(value)) return formatSessionDateLabelFromKey(value);
  return value;
}

export function auditFacetValue(row: AuditRow, column: AuditFilterColumn, ctx: AuditTableContext): string {
  switch (column) {
    case "when":
      return sessionLocalDateKey(row.occurredAt, ctx.timeZone);
    case "who":
      return row.who;
    case "type":
      return typeLabel(row.type);
    case "device":
      return ctx.deviceNameFor(row.deviceId);
    case "session":
      return row.sessionId ?? "None";
    case "summary":
      return row.detail;
  }
}

type RowFacets = ReadonlyMap<AuditFilterColumn, string>;

function rowFacets(row: AuditRow, ctx: AuditTableContext): RowFacets {
  return new Map(AUDIT_FILTER_COLUMNS.map((column) => [column, auditFacetValue(row, column, ctx)]));
}

function passes(facets: RowFacets, column: AuditFilterColumn, filters: AuditColumnFilters): boolean {
  const selected = filters.values.get(column);
  if (!selected || selected.size === 0) return true;
  return selected.has(facets.get(column) ?? "");
}

export function filterAuditRows(rows: readonly AuditRow[], filters: AuditColumnFilters, ctx: AuditTableContext): AuditRow[] {
  return rows.filter((row) => {
    const facets = rowFacets(row, ctx);
    return AUDIT_FILTER_COLUMNS.every((column) => passes(facets, column, filters));
  });
}

export type AuditFilterOption = { readonly value: string; readonly label: string; readonly count: number };

export type AuditColumnFacet = {
  readonly options: readonly AuditFilterOption[];
};

export type AuditFacets = {
  readonly rows: readonly AuditRow[];
  readonly columns: ReadonlyMap<AuditFilterColumn, AuditColumnFacet>;
};

export function computeAuditFacets(rows: readonly AuditRow[], filters: AuditColumnFilters, ctx: AuditTableContext): AuditFacets {
  const counts = new Map<AuditFilterColumn, Map<string, number>>(AUDIT_FILTER_COLUMNS.map((column) => [column, new Map()]));
  const passing: AuditRow[] = [];
  for (const row of rows) {
    const facets = rowFacets(row, ctx);
    const failed = AUDIT_FILTER_COLUMNS.filter((column) => !passes(facets, column, filters));
    if (failed.length === 0) passing.push(row);
    for (const column of AUDIT_FILTER_COLUMNS) {
      const counted = failed.length === 0 || (failed.length === 1 && failed[0] === column);
      const tally = counts.get(column);
      if (!tally) continue;
      const value = facets.get(column) ?? "";
      tally.set(value, (tally.get(value) ?? 0) + (counted ? 1 : 0));
    }
  }
  const columns = new Map<AuditFilterColumn, AuditColumnFacet>();
  for (const column of AUDIT_FILTER_COLUMNS) {
    const tally = counts.get(column) ?? new Map<string, number>();
    columns.set(column, {
      options: [...tally.keys()].sort((left, right) => collator.compare(left, right)).map((value) => ({
        value,
        label: auditFilterDisplayLabel(column, value),
        count: tally.get(value) ?? 0,
      })),
    });
  }
  return { rows: passing, columns };
}

function filterSignature(filters: AuditColumnFilters): string {
  const parts: string[] = [];
  for (const column of AUDIT_FILTER_COLUMNS) {
    const selected = filters.values.get(column);
    if (!selected || selected.size === 0) continue;
    parts.push(column, ...[...selected].sort());
  }
  return parts.join("\u0000");
}

function contextSignature(ctx: AuditTableContext): string {
  return `${ctx.timeZone ?? ""}\u0000${ctx.deviceNamesToken}`;
}

export function createAuditFacetMemo(compute: typeof computeAuditFacets = computeAuditFacets) {
  let last: { rows: readonly AuditRow[]; signature: string; ctxSig: string; facets: AuditFacets } | null = null;
  return (rows: readonly AuditRow[], filters: AuditColumnFilters, ctx: AuditTableContext): AuditFacets => {
    const signature = filterSignature(filters);
    const ctxSig = contextSignature(ctx);
    if (last && last.rows === rows && last.signature === signature && last.ctxSig === ctxSig) return last.facets;
    const facets = compute(rows, filters, ctx);
    last = { rows, signature, ctxSig, facets };
    return facets;
  };
}

export function toggleAuditColumnFilter(filters: AuditColumnFilters, column: AuditFilterColumn, value: string): AuditColumnFilters {
  const values = new Map(filters.values);
  const selected = new Set(values.get(column) ?? []);
  if (selected.has(value)) selected.delete(value);
  else selected.add(value);
  if (selected.size === 0) values.delete(column);
  else values.set(column, selected);
  return { values };
}

export function selectedAuditColumnFilterCount(filters: AuditColumnFilters, column: AuditFilterColumn): number {
  return filters.values.get(column)?.size ?? 0;
}

export function hasAuditColumnFilters(filters: AuditColumnFilters): boolean {
  return [...filters.values.values()].some((selected) => selected.size > 0);
}

function sortKey(row: AuditRow, column: AuditFilterColumn, ctx: AuditTableContext): string {
  if (column === "when") return row.occurredAt;
  return auditFacetValue(row, column, ctx);
}

export function sortAuditRows(rows: readonly AuditRow[], sort: AuditColumnSort, ctx: AuditTableContext): AuditRow[] {
  const copy = [...rows];
  const direction = sort.direction === "asc" ? 1 : -1;
  copy.sort((left, right) => collator.compare(sortKey(left, sort.column, ctx), sortKey(right, sort.column, ctx)) * direction);
  return copy;
}

export const AUDIT_COLUMN_LABELS: Readonly<Record<AuditFilterColumn, string>> = {
  when: "When",
  who: "Who",
  type: "Type",
  device: "Device",
  session: "Session",
  summary: "Summary",
};
